import React from 'react';
import { useForm, useController } from 'react-hook-form';
import {
  BoxV2 as Box,
  ButtonV2 as Button,
  Flex,
  TextInputV2 as TextInput,
} from 'portal-commons';
import { SenderId } from '@/apis/core';
import {
  createMaxLengthValidator,
  createRequiredValidator,
  isValidInput,
} from '@/utils/validators';

interface SuspendSenderIdFormProps {
  senderId: SenderId;
  onCancel?: () => void;
  onSubmit?: (explanation: string) => void;
}

interface FormValues {
  explanation: string;
}

const SuspendSenderIdForm: React.FC<SuspendSenderIdFormProps> = ({
  senderId,
  onCancel,
  onSubmit,
}) => {
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      explanation: '',
    },
  });

  const {
    field: {
      value: explanation,
      onChange: setExplanation,
      onBlur: validateExplanation,
    },
    fieldState: { error: explanationError },
  } = useController({
    control,
    name: 'explanation',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Explanation is required'),
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(2048),
      },
    },
  });

  const handleValid = (data: FormValues) => {
    onSubmit?.(data.explanation);
  };

  return (
    <form
      style={{ height: '100%' }}
      noValidate
      onSubmit={handleSubmit(handleValid)}
      data-testid="suspendSenderIdForm"
    >
      <Flex
        sx={{
          flexDirection: 'column',
          gap: 'xl',
          height: '100%',
          color: 't.black70',
          fontWeight: 400,
          lineHeight: 'normal',
        }}
      >
        <Flex
          sx={{
            flex: 1,
            flexDirection: 'column',
            gap: 'xl',
            overflow: 'auto',
          }}
        >
          <Box
            sx={{
              color: 't.black90',
              fontWeight: 500,
              fontSize: '28px',
            }}
            data-testid="suspendDrawerTitle"
          >
            Suspend Sender ID
          </Box>

          <Box sx={{ fontSize: 'H300', color: '#4D5659', mb: -8 }}>
            * Required field
          </Box>

          <TextInput
            readOnly
            label="Sender ID"
            value={senderId.senderIdValue}
            data-testid="readOnlySenderId"
          />
          <TextInput
            readOnly
            label="DCA"
            value={senderId.dcaName || 'Not Assigned'}
            data-testid="readOnlyDca"
          />
          <TextInput
            readOnly
            label="Program ID"
            value={senderId.campaignUid || 'N/A'}
            data-testid="readOnlyProgramId"
          />

          <TextInput
            required
            multiline
            minRows={3}
            maxRows={3}
            label="Explanation"
            error={!!explanationError}
            data-testid="explanationInput"
            note={
              !!explanationError
                ? explanationError?.message
                : 'Please provide details.'
            }
            value={explanation}
            onChange={setExplanation}
            onBlur={validateExplanation}
          />
        </Flex>

        <Flex sx={{ justifyContent: 'flex-end', gap: 'l' }}>
          <Button
            type="button"
            shape="square"
            variant="outline"
            size="lg"
            onClick={onCancel}
            data-testid="cancelButton"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            shape="square"
            size="lg"
            disabled={!isValid}
            data-testid="submitButton"
          >
            Suspend
          </Button>
        </Flex>
      </Flex>
    </form>
  );
};

export default SuspendSenderIdForm;
